import { useEffect, type FC, useState } from 'react'

import { useFormContext } from 'react-hook-form'

import styled from '@emotion/styled'

import TextArea from '@segment/matcha/components/TextArea'
import CheckboxField from '@segment/matcha/components/CheckboxField'
import ErrorSlot from '@segment/matcha/components/Input/ErrorSlot'
import Typography from '@segment/matcha/components/Typography'

export const warehouseCheckboxesMessages = {
  required: `Please select at least one warehouse.`,
}

export const warehouseMessages = {
  required: `Please provide your warehouse.`,
  invalid: `Please provide a valid warehouse.`,
}
interface WarehouseFieldProps {
  data: string[]
}

/**
 * Warehouse field
 */
const WarehouseField: FC<WarehouseFieldProps> = ({ data }) => {
  const [other, setOther] = useState<boolean>(false)
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const [checkBoxesSelected, setCheckBoxesSelected] = useState<string[]>(new Array(data.length).fill(``))

  const {
    register,
    clearErrors,
    setError,
    formState: { errors },
  } = useFormContext()

  const checkSelection = (idx: number, value: string, checked: boolean) => {
    const arr = [...checkBoxesSelected]
    arr[idx] = checked ? value : ``
    setCheckBoxesSelected(arr)
  }

  useEffect(() => {
    const isValid = checkBoxesSelected.some((c) => c)
    if (isValid) {
      clearErrors(`selectedWarehouses`)
    } else if (!errors.selectedWarehouses && isDirty) {
      setError(`selectedWarehouses`, { type: `required`, message: warehouseCheckboxesMessages.required })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkBoxesSelected])

  return (
    <>
      <CheckBoxesWrapper>
        <Label variant="formLabel"> Which warehouse does your business use? </Label>
        {[...data, `Other`].map((title, index) => (
          <CheckboxField
            key={`${title}-${index}`}
            label={title}
            value={title}
            {...register(`selectedWarehouses`)}
            onChange={(checkField) => {
              setIsDirty(true)
              const { value, checked } = checkField.target
              if (value === `Other`) setOther(checked)
              checkSelection(index, value, checked)
            }}
          />
        ))}
        {errors.selectedWarehouses ? <ErrorSlot errorText={errors.selectedWarehouses.message} /> : null}
      </CheckBoxesWrapper>

      {other ? (
        <TextArea
          id="otherWarehouse"
          label="Please share your warehouse"
          placeholder="Please share your warehouse"
          aria-label="Please share your warehouse"
          error={Boolean(errors.otherWarehouse)}
          errorText={errors.otherWarehouse?.message}
          expanded={true}
          {...register(`otherWarehouse`, {
            required: warehouseMessages.required,
          })}
        />
      ) : null}
    </>
  )
}

export const Label = styled(Typography)`
  margin: 0;
  display: block;
  color: ${({ theme }) => theme.palette.twilio.gray[80]};
`

export const CheckBoxesWrapper = styled.div`
  position: relative;
  display: grid;
  row-gap: 18px;
`

export default WarehouseField
